import React, { useCallback, useContext, useEffect, useState } from "react";
import { Alert, Button, Input, InputNumber, Radio, Skeleton, message } from "antd";
import { CrownOutlined, DollarOutlined, KeyOutlined, ReloadOutlined } from "@ant-design/icons";
import { AuthContext } from "../../context/auth-context/AuthContext";
import apiService from "../../services/apiServices";

const usd = new Intl.NumberFormat("es-AR", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const LIMITES = ["minutosPorSesion", "minutosPorMes", "concurrenciaMax", "sesionesPorIPporHora", "costoPorMinuto"];
const limitesDesde = (a) => Object.fromEntries(LIMITES.map((k) => [k, a ? a[k] : null]));

const PROVEEDORES = [
  {
    campo: "elevenlabsApiKey",
    clave: "elevenlabs",
    nombre: "ElevenLabs",
    ayuda: "Desde una misma cuenta de ElevenLabs se puede crear una key por cliente; el consumo de cada key se ve por separado.",
  },
  {
    campo: "liveavatarApiKey",
    clave: "liveavatar",
    nombre: "LiveAvatar",
    ayuda: "Opcional. Si el cliente tiene su propia cuenta de LiveAvatar, los minutos del avatar se cobran ahí.",
  },
];

//lo que abonó el cliente; lo cambia solo el equipo de Griftin (SUPER_ADMIN)
const PLANES = {
  basico: { nombre: "Básico", detalle: "Guía 3D animada con la voz del agente." },
  premium: { nombre: "Premium", detalle: "Guía en video que parece una persona real (LiveAvatar)." },
};

const Numero = ({ etiqueta, ayuda, sufijo, ...props }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "var(--inm-texto-2)" }}>
    {etiqueta}
    <InputNumber style={{ width: "100%" }} addonAfter={sufijo} {...props} />
    {ayuda && <span className="inm-tenue" style={{ fontSize: 12 }}>{ayuda}</span>}
  </label>
);

/**
 * Uso del mes, costo estimado, límites de uso y keys de los proveedores de una experiencia.
 * Habla con /asistente/ajustes, que exige login y ser dueño: las keys no viajan en la experiencia.
 */
export const UsoYCostos = ({ experienciaId, estadoGlobal, onTopeSesion }) => {
  const { handleRefreshToken, authState } = useContext(AuthContext);
  const esEquipo = authState?.role === "SUPER_ADMIN";
  const [ajustes, setAjustes] = useState(null);
  const [error, setError] = useState(null);
  const [limites, setLimites] = useState(limitesDesde(null));
  const [guardando, setGuardando] = useState(null);
  const [claves, setClaves] = useState({ elevenlabsApiKey: "", liveavatarApiKey: "" });
  const [plan, setPlan] = useState(null);

  const aplicar = useCallback((a) => {
    setAjustes(a);
    setLimites(limitesDesde(a));
    //un servidor sin planes no manda el campo: esas experiencias siguen con LiveAvatar
    setPlan(a?.plan || "premium");
  }, []);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const jwt = await handleRefreshToken();
      aplicar(await apiService.getAjustesAsistente(jwt, experienciaId));
    } catch (e) {
      setError(
        e.status === 403 || e.status === 401
          ? "No tenés permisos para ver el uso de esta experiencia."
          : "No pudimos cargar el uso y los límites."
      );
    }
  }, [handleRefreshToken, experienciaId, aplicar]);

  useEffect(() => {
    setAjustes(null);
    setClaves({ elevenlabsApiKey: "", liveavatarApiKey: "" });
    cargar();
  }, [cargar]);

  const guardar = async (cambios, que, aviso) => {
    setGuardando(que);
    try {
      const jwt = await handleRefreshToken();
      const nuevos = await apiService.guardarAjustesAsistente(jwt, experienciaId, cambios);
      aplicar(nuevos);
      if (cambios.minutosPorSesion !== undefined && onTopeSesion) onTopeSesion(nuevos.minutosPorSesion);
      message.success(aviso);
      return true;
    } catch (e) {
      message.error(
        e.status === 400 && cambios.plan !== undefined && !ajustes?.plan
          ? "El servidor todavía no admite planes: falta desplegar la actualización de la API."
          : e.status === 400 ? "Revisá los valores: alguno está fuera de rango."
          : e.status === 403 ? "No tenés permisos para este cambio."
          : "No se pudo guardar."
      );
      return false;
    } finally {
      setGuardando(null);
    }
  };

  const guardarLimites = () => {
    const cambios = {};
    LIMITES.forEach((k) => {
      if (limites[k] !== ajustes[k]) cambios[k] = limites[k] === undefined ? null : limites[k];
    });
    //los números vacíos no se mandan: solo el costo se puede borrar
    LIMITES.filter((k) => k !== "costoPorMinuto").forEach((k) => {
      if (cambios[k] === null) delete cambios[k];
    });
    guardar(cambios, "limites", "Límites guardados");
  };

  const guardarClave = async (campo, valor, aviso) => {
    if (await guardar({ [campo]: valor }, campo, aviso)) setClaves((c) => ({ ...c, [campo]: "" }));
  };

  if (error) {
    return (
      <section className="inm-caja" aria-labelledby="asis-uso">
        <h2 id="asis-uso" className="inm-h2">Uso y costos</h2>
        <Alert type="warning" showIcon message={error} action={<Button size="small" onClick={cargar}>Reintentar</Button>} />
      </section>
    );
  }

  const c = ajustes?.consumo;
  const costo = ajustes?.costoPorMinuto;
  const hayCosto = typeof costo === "number";
  const modificados = ajustes && LIMITES.some((k) => limites[k] !== ajustes[k]);

  return (
    <section className="inm-caja" aria-labelledby="asis-uso">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h2 id="asis-uso" className="inm-h2" style={{ flexGrow: 1 }}>
          <DollarOutlined /> Uso y costos
        </h2>
        <Button type="text" size="small" icon={<ReloadOutlined />} aria-label="Actualizar uso" onClick={cargar} />
      </div>

      {!ajustes ? (
        <Skeleton active paragraph={{ rows: 5 }} title={false} />
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, borderBottom: "1px solid var(--inm-borde)", paddingBottom: 16 }}>
            <h3 className="inm-h3" style={{ margin: 0, fontSize: 14, color: "var(--inm-texto)" }}>
              <CrownOutlined /> Plan del asistente
            </h3>
            {esEquipo ? (
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <Radio.Group value={plan} disabled={guardando === "plan"} onChange={(e) => setPlan(e.target.value)}>
                  {Object.entries(PLANES).map(([k, p]) => (
                    <Radio key={k} value={k}>
                      <b>{p.nombre}</b> <span className="inm-tenue" style={{ fontSize: 12 }}>· {p.detalle}</span>
                    </Radio>
                  ))}
                </Radio.Group>
                <Button
                  type="primary"
                  className="inm-btn-asistente"
                  disabled={plan === (ajustes.plan || "premium")}
                  loading={guardando === "plan"}
                  onClick={() => guardar({ plan }, "plan", `Plan ${PLANES[plan].nombre} guardado`)}
                >
                  Guardar plan
                </Button>
              </div>
            ) : (
              <div className="inm-dato">
                {PLANES[plan]?.detalle}
                <b>{PLANES[plan]?.nombre}</b>
              </div>
            )}
            <span className="inm-tenue" style={{ fontSize: 12 }}>
              {esEquipo
                ? "Lo que contrató el cliente. Se aplica en la próxima charla que abra un visitante."
                : "Para cambiar de plan, escribile al equipo de Griftin."}
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <h3 className="inm-h3" style={{ margin: 0, fontSize: 14, color: "var(--inm-texto)" }}>Este mes</h3>
              <div className="inm-dato">
                Minutos de charla
                <b>
                  {c.minutosUsados} / {ajustes.minutosPorMes}
                </b>
              </div>
              <div className="inm-medidor" role="meter" aria-label="Minutos usados del mes" aria-valuemin={0} aria-valuemax={ajustes.minutosPorMes} aria-valuenow={c.minutosUsados}>
                <i style={{ width: `${ajustes.minutosPorMes ? Math.min(100, Math.round((c.minutosUsados / ajustes.minutosPorMes) * 100)) : 0}%` }} />
              </div>
              <div className="inm-dato">
                Charlas <b>{c.sesiones}</b>
              </div>
              <div className="inm-dato">
                Charlas en curso
                <b>
                  {c.vivas} / {ajustes.concurrenciaMax}
                </b>
              </div>
              {estadoGlobal && (
                <div className="inm-dato">
                  En curso en todo Inmersial
                  <b>
                    {estadoGlobal.vivas} / {estadoGlobal.concurrenciaMax}
                  </b>
                </div>
              )}
              <div style={{ borderTop: "1px solid var(--inm-borde)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                {hayCosto ? (
                  <>
                    <div className="inm-dato">
                      Costo estimado del mes <b>{usd.format(c.minutosUsados * costo)}</b>
                    </div>
                    <div className="inm-dato">
                      Promedio por charla <b>{c.sesiones ? usd.format((c.minutosUsados * costo) / c.sesiones) : "—"}</b>
                    </div>
                    <div className="inm-dato">
                      Máximo del mes (todos los minutos) <b>{usd.format(ajustes.minutosPorMes * costo)}</b>
                    </div>
                  </>
                ) : (
                  <p className="inm-tenue" style={{ margin: 0 }}>Cargá el costo por minuto para ver el costo estimado.</p>
                )}
              </div>
              <p className="inm-tenue" style={{ margin: 0, fontSize: 12 }}>
                Es una estimación: minutos de charla × costo por minuto. Lo que se cobra de verdad está en las cuentas de
                LiveAvatar y ElevenLabs. El conteo vuelve a cero si se reinicia el servidor.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <h3 className="inm-h3" style={{ margin: 0, fontSize: 14, color: "var(--inm-texto)" }}>Límites de uso</h3>
              <Numero
                etiqueta="Duración máxima de cada charla"
                sufijo="min"
                min={1}
                max={60}
                precision={0}
                value={limites.minutosPorSesion}
                onChange={(v) => setLimites((l) => ({ ...l, minutosPorSesion: v }))}
                ayuda="Al llegar a este tiempo la charla se cierra sola."
              />
              <Numero
                etiqueta="Minutos por mes"
                sufijo="min"
                min={0}
                max={1000000}
                precision={0}
                value={limites.minutosPorMes}
                onChange={(v) => setLimites((l) => ({ ...l, minutosPorMes: v }))}
                ayuda="Es el tope de gasto: cuando se agotan, el asistente no atiende hasta el mes siguiente."
              />
              <Numero
                etiqueta="Charlas al mismo tiempo"
                min={1}
                max={ajustes.concurrenciaTecho}
                precision={0}
                value={limites.concurrenciaMax}
                onChange={(v) => setLimites((l) => ({ ...l, concurrenciaMax: v }))}
                ayuda={`Hasta ${ajustes.concurrenciaTecho}, que es el máximo del servidor.`}
              />
              <Numero
                etiqueta="Charlas por hora desde un mismo dispositivo"
                min={1}
                max={1000}
                precision={0}
                value={limites.sesionesPorIPporHora}
                onChange={(v) => setLimites((l) => ({ ...l, sesionesPorIPporHora: v }))}
                ayuda="Evita que alguien abra charlas una detrás de otra y consuma los minutos del mes."
              />
              <Numero
                etiqueta="Costo por minuto"
                sufijo="USD"
                min={0}
                max={100}
                step={0.01}
                precision={3}
                value={limites.costoPorMinuto}
                placeholder="Sin cargar"
                onChange={(v) => setLimites((l) => ({ ...l, costoPorMinuto: v }))}
                ayuda="Sumá lo que cobran LiveAvatar y ElevenLabs por minuto con el plan contratado."
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="primary"
                  className="inm-btn-asistente"
                  disabled={!modificados}
                  loading={guardando === "limites"}
                  onClick={guardarLimites}
                >
                  Guardar límites
                </Button>
              </div>
            </div>
          </div>

          <div style={{ borderTop: "1px solid var(--inm-borde)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>
            <h3 className="inm-h3" style={{ margin: 0, fontSize: 14, color: "var(--inm-texto)" }}>
              <KeyOutlined /> Cuentas de los proveedores
            </h3>
            <p className="inm-tenue" style={{ margin: 0 }}>
              Con una key propia, lo que consume esta experiencia se cobra y se ve por separado en la cuenta del proveedor.
              Si la dejás vacía se usa la cuenta de Inmersial. Las keys se guardan en el servidor y no se vuelven a mostrar.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
              {PROVEEDORES.map((p) => {
                const est = ajustes.claves[p.clave];
                return (
                  <div key={p.campo} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 13 }}>
                      <span style={{ color: "var(--inm-texto-2)" }}>Key de {p.nombre}</span>
                      <span className={`inm-pill ${est.propia ? "inm-pill--asistente" : "inm-pill--neutro"}`}>
                        {est.propia ? `Propia · termina en ${est.final}` : "Cuenta de Inmersial"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Input.Password
                        value={claves[p.campo]}
                        autoComplete="off"
                        placeholder={est.propia ? "Pegá una key nueva para reemplazarla" : "Pegá la key de este cliente"}
                        aria-label={`Key de ${p.nombre}`}
                        onChange={(e) => setClaves((k) => ({ ...k, [p.campo]: e.target.value.trim() }))}
                      />
                      <Button
                        disabled={!claves[p.campo]}
                        loading={guardando === p.campo}
                        onClick={() => guardarClave(p.campo, claves[p.campo], `Key de ${p.nombre} guardada`)}
                      >
                        Guardar
                      </Button>
                      {est.propia && (
                        <Button danger onClick={() => guardarClave(p.campo, null, `Key de ${p.nombre} quitada`)}>
                          Quitar
                        </Button>
                      )}
                    </div>
                    <span className="inm-tenue" style={{ fontSize: 12 }}>{p.ayuda}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </section>
  );
};
